import { useState, useEffect } from "react";
import {
  getOperationalRestrictions, createOperationalRestriction,
  updateOperationalRestriction, deleteOperationalRestriction
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  ShieldAlert, Plus, Edit2, Trash2, Loader2, Info, AlertTriangle, OctagonAlert, CheckCircle2, Clock
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

export const SEVERITY_META = {
  info: { label: "Info", badge: "bg-sky-500", icon: Info,
    box: "border-sky-300 bg-sky-50 dark:bg-sky-900/20 dark:border-sky-800" },
  warning: { label: "Warning", badge: "bg-amber-500", icon: AlertTriangle,
    box: "border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800" },
  critical: { label: "Critical", badge: "bg-red-600", icon: OctagonAlert,
    box: "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800" },
};

// Human-readable time-window label. Empty times = all day. If end <= start the
// window crosses midnight into the next morning.
export const restrictionWindowText = (r) => {
  const st = (r.start_time || "").trim();
  const et = (r.end_time || "").trim();
  if (!st || !et) return "All day";
  const suffix = st >= et ? " next day" : "";
  return `${st}–${et}${suffix} (local)`;
};

export default function OperationalRestrictionScheduler({ onRestrictionChange }) {
  const [restrictions, setRestrictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    start_date: "", end_date: "", reason: "", severity: "warning", start_time: "", end_time: ""
  });

  const fetchRestrictions = async () => {
    setLoading(true);
    try {
      const res = await getOperationalRestrictions();
      setRestrictions(res.data.restrictions || []);
    } catch (error) {
      console.error("Failed to fetch restrictions:", error);
      toast.error("Failed to load operational restrictions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRestrictions(); }, []);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const resetForm = () => setFormData({ start_date: "", end_date: "", reason: "", severity: "warning", start_time: "", end_time: "" });

  const handleCreate = async () => {
    if (!formData.start_date || !formData.end_date || !formData.reason.trim()) {
      toast.error("Please fill in the dates and a reason");
      return;
    }
    setSaving(true);
    try {
      await createOperationalRestriction(formData);
      toast.success("Operational restriction scheduled");
      setShowAddDialog(false);
      resetForm();
      fetchRestrictions();
      onRestrictionChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule restriction");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateOperationalRestriction(selected.id, {
        start_date: formData.start_date,
        end_date: formData.end_date,
        reason: formData.reason,
        severity: formData.severity,
        start_time: formData.start_time,
        end_time: formData.end_time,
      });
      toast.success("Restriction updated");
      setShowEditDialog(false);
      setSelected(null);
      resetForm();
      fetchRestrictions();
      onRestrictionChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update restriction");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await deleteOperationalRestriction(selected.id);
      toast.success("Restriction deleted");
      setShowDeleteConfirm(false);
      setSelected(null);
      fetchRestrictions();
      onRestrictionChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete restriction");
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (r) => {
    setSelected(r);
    setFormData({
      start_date: r.start_date, end_date: r.end_date,
      reason: r.reason || "", severity: r.severity || "warning",
      start_time: r.start_time || "", end_time: r.end_time || ""
    });
    setShowEditDialog(true);
  };

  const openDeleteConfirm = (r) => { setSelected(r); setShowDeleteConfirm(true); };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-red-600 text-white"><ShieldAlert size={12} className="mr-1" /> Active</Badge>;
      case "upcoming":
        return <Badge className="bg-amber-500 text-white"><Clock size={12} className="mr-1" /> Upcoming</Badge>;
      case "expired":
        return <Badge variant="outline" className="text-gray-500"><CheckCircle2 size={12} className="mr-1" /> Ended</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const SevIcon = (SEVERITY_META[formData.severity] || SEVERITY_META.warning).icon;

  return (
    <Card className="bg-white dark:bg-[#1e293b]" data-testid="operational-restriction-scheduler">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ShieldAlert size={20} className="text-[#d4af37]" />
            Operational Restrictions
          </span>
          <Button
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            size="sm"
            className="gap-2 bg-[#d4af37] hover:bg-[#b8962d]"
            data-testid="add-restriction-btn"
          >
            <Plus size={16} />
            Add Restriction
          </Button>
        </CardTitle>
        <p className="text-xs text-[#64748b]">e.g. airport strike, reduced operations. Shown on the dashboard, in daily planning, and on affected flights.</p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin mr-2" size={24} /> Loading restrictions...
          </div>
        ) : restrictions.length === 0 ? (
          <div className="text-center py-8 text-[#64748b]">
            <ShieldAlert size={40} className="mx-auto mb-2 opacity-30" />
            <p>No operational restrictions</p>
            <p className="text-sm mt-1">Click "Add Restriction" to schedule one</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {restrictions.map((r) => {
              const meta = SEVERITY_META[r.severity] || SEVERITY_META.warning;
              const MetaIcon = meta.icon;
              return (
                <div key={r.id} className={`p-3 rounded-lg border transition-all ${meta.box}`} data-testid={`restriction-${r.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${meta.badge} text-white font-bold`}>
                        <MetaIcon size={12} className="mr-1" />{meta.label}
                      </Badge>
                      {getStatusBadge(r.status)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                        onClick={() => openEditDialog(r)} data-testid={`edit-restriction-${r.id}`}>
                        <Edit2 size={14} />
                      </Button>
                      <Button variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openDeleteConfirm(r)} data-testid={`delete-restriction-${r.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  <div className="text-sm text-[#334155] dark:text-gray-300">
                    <div className="font-medium mb-1">{r.reason}</div>
                    <div className="flex items-center gap-2 text-[#64748b] dark:text-gray-400">
                      <span className="font-medium">Period:</span>
                      <span>{format(parseISO(r.start_date), "MMM d, yyyy")} - {format(parseISO(r.end_date), "MMM d, yyyy")}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#64748b] dark:text-gray-400">
                      <span className="font-medium">Hours:</span>
                      <span>{restrictionWindowText(r)}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      Created by {r.created_by} on {format(parseISO(r.created_at), "MMM d, yyyy HH:mm")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-white dark:bg-[#1e293b]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert size={20} className="text-[#d4af37]" />
              Add Operational Restriction
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Severity *</Label>
              <Select value={formData.severity} onValueChange={(v) => handleInputChange("severity", v)}>
                <SelectTrigger data-testid="severity-select">
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      <SevIcon size={14} /> {(SEVERITY_META[formData.severity] || SEVERITY_META.warning).label}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input type="date" value={formData.start_date} min={today}
                  onChange={(e) => handleInputChange("start_date", e.target.value)}
                  data-testid="restriction-start-date-input" />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input type="date" value={formData.end_date} min={formData.start_date || today}
                  onChange={(e) => handleInputChange("end_date", e.target.value)}
                  data-testid="restriction-end-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time (optional)</Label>
                <Input type="time" value={formData.start_time}
                  onChange={(e) => handleInputChange("start_time", e.target.value)}
                  data-testid="restriction-start-time-input" />
              </div>
              <div className="space-y-2">
                <Label>End Time (optional)</Label>
                <Input type="time" value={formData.end_time}
                  onChange={(e) => handleInputChange("end_time", e.target.value)}
                  data-testid="restriction-end-time-input" />
              </div>
            </div>
            <p className="text-xs text-[#64748b]">
              Leave times empty for an all-day restriction. If the end time is earlier than the start
              time (e.g. 22:00 → 08:00), the window is treated as overnight into the next morning.
            </p>
            <div className="space-y-2">
              <Label>Reason / Description *</Label>
              <Textarea
                placeholder="e.g., National ATC strike — expect reduced operations and possible delays"
                value={formData.reason}
                onChange={(e) => handleInputChange("reason", e.target.value)}
                className="min-h-[80px]"
                data-testid="restriction-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate}
              disabled={saving || !formData.start_date || !formData.end_date || !formData.reason.trim()}
              className="bg-[#d4af37] hover:bg-[#b8962d]" data-testid="save-restriction-btn">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Add Restriction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white dark:bg-[#1e293b]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 size={20} className="text-[#d4af37]" /> Edit Operational Restriction
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Severity *</Label>
              <Select value={formData.severity} onValueChange={(v) => handleInputChange("severity", v)}>
                <SelectTrigger data-testid="edit-severity-select">
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      <SevIcon size={14} /> {(SEVERITY_META[formData.severity] || SEVERITY_META.warning).label}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input type="date" value={formData.start_date}
                  onChange={(e) => handleInputChange("start_date", e.target.value)}
                  data-testid="edit-restriction-start-date-input" />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input type="date" value={formData.end_date} min={formData.start_date}
                  onChange={(e) => handleInputChange("end_date", e.target.value)}
                  data-testid="edit-restriction-end-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time (optional)</Label>
                <Input type="time" value={formData.start_time}
                  onChange={(e) => handleInputChange("start_time", e.target.value)}
                  data-testid="edit-restriction-start-time-input" />
              </div>
              <div className="space-y-2">
                <Label>End Time (optional)</Label>
                <Input type="time" value={formData.end_time}
                  onChange={(e) => handleInputChange("end_time", e.target.value)}
                  data-testid="edit-restriction-end-time-input" />
              </div>
            </div>
            <p className="text-xs text-[#64748b]">
              Leave times empty for an all-day restriction. End earlier than start (e.g. 22:00 → 08:00)
              means overnight into the next morning.
            </p>
            <div className="space-y-2">
              <Label>Reason / Description *</Label>
              <Textarea value={formData.reason}
                onChange={(e) => handleInputChange("reason", e.target.value)}
                className="min-h-[80px]"
                data-testid="edit-restriction-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate}
              disabled={saving || !formData.start_date || !formData.end_date || !formData.reason.trim()}
              className="bg-[#d4af37] hover:bg-[#b8962d]" data-testid="update-restriction-btn">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Update Restriction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 size={20} className="text-red-600" /> Delete Operational Restriction
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this restriction?
              {selected?.reason && <><br /><br /><span className="italic">"{selected.reason}"</span></>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelected(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700"
              disabled={saving} data-testid="confirm-delete-restriction-btn">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
